import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import Collapse from "@material-ui/core/Collapse";
import List from "@material-ui/core/List";
import Tooltip from "@material-ui/core/Tooltip";
import { Badge } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FolderOutlinedIcon from "@material-ui/icons/FolderOutlined";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";
import ViewKanbanIcon from "@material-ui/icons/ViewColumn";
import FlashOnOutlinedIcon from "@material-ui/icons/FlashOnOutlined";
import DynamicFeedOutlinedIcon from "@material-ui/icons/DynamicFeedOutlined";
import AlarmOutlinedIcon from "@material-ui/icons/AlarmOutlined";
import AndroidOutlinedIcon from "@material-ui/icons/AndroidOutlined";
import GroupOutlinedIcon from "@material-ui/icons/GroupOutlined";
import ForumIcon from "@material-ui/icons/Forum";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";

function ListItemLink(props) {
  const { icon, primary, to, className, drawerOpen } = props;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      {/* Con el drawer retraído, el texto queda recortado — el tooltip
          muestra el nombre de la sección al pasar el cursor. Title vacío
          con el drawer abierto desactiva el tooltip (ya se ve el texto). */}
      <Tooltip title={drawerOpen ? "" : primary} placement="right">
        <ListItem button component={renderLink} className={className}>
          {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
          <ListItemText primary={primary} />
        </ListItem>
      </Tooltip>
    </li>
  );
}

const AUTOMATION_PATHS = ["/autoReplyRules", "/autoReplyFlows", "/followUpRules"];
const AGENT_IA_PATHS = ["/agentIa/agents", "/agentIa/training", "/agentIa/documents"];

const useStyles = makeStyles((theme) => ({
  nested: {
    paddingLeft: theme.spacing(4),
  },
}));

const MainListItems = (props) => {
  const { drawerClose, drawerOpen } = props;
  const classes = useStyles();
  const location = useLocation();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  // Abierta por defecto si ya estás en alguna de sus pantallas.
  const [automationsOpen, setAutomationsOpen] = useState(
    AUTOMATION_PATHS.includes(location.pathname)
  );
  const [agentIaOpen, setAgentIaOpen] = useState(
    AGENT_IA_PATHS.includes(location.pathname)
  );

  const handleToggleAutomations = (e) => {
    // No debe cerrar el drawer (mobile) como sí hacen los links reales,
    // porque acá no se navega a ningún lado, solo se despliega la carpeta.
    e.stopPropagation();
    setAutomationsOpen((open) => !open);
  };

  const handleToggleAgentIa = (e) => {
    e.stopPropagation();
    setAgentIaOpen((open) => !open);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  return (
    <div onClick={drawerClose}>
      <ListItemLink
        to="/"
        primary="Dashboard"
        icon={<DashboardOutlinedIcon />}
        drawerOpen={drawerOpen}
      />
      <ListItemLink
        to="/connections"
        primary={i18n.t("mainDrawer.listItems.connections")}
        icon={
          <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
            <SyncAltIcon />
          </Badge>
        }
        drawerOpen={drawerOpen}
      />
      <ListItemLink
        to="/tickets"
        primary={i18n.t("mainDrawer.listItems.tickets")}
        icon={<WhatsAppIcon />}
        drawerOpen={drawerOpen}
      />
      <ListItemLink
        to="/kanban"
        primary="Embudo"
        icon={<ViewKanbanIcon />}
        drawerOpen={drawerOpen}
      />
      <ListItemLink
        to="/contacts"
        primary={i18n.t("mainDrawer.listItems.contacts")}
        icon={<ContactPhoneOutlinedIcon />}
        drawerOpen={drawerOpen}
      />
      <ListItemLink
        to="/quickAnswers"
        primary={i18n.t("mainDrawer.listItems.quickAnswers")}
        icon={<QuestionAnswerOutlinedIcon />}
        drawerOpen={drawerOpen}
      />
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <Divider />
            <ListSubheader inset>
              {i18n.t("mainDrawer.listItems.administration")}
            </ListSubheader>
            <ListItemLink
              to="/users"
              primary={i18n.t("mainDrawer.listItems.users")}
              icon={<PeopleAltOutlinedIcon />}
              drawerOpen={drawerOpen}
            />
            <ListItemLink
              to="/queues"
              primary={i18n.t("mainDrawer.listItems.queues")}
              icon={<AccountTreeOutlinedIcon />}
              drawerOpen={drawerOpen}
            />
            <Tooltip
              title={
                drawerOpen ? "" : (
                  <>
                    <div>{i18n.t("mainDrawer.listItems.automations")}</div>
                    <div>• {i18n.t("mainDrawer.listItems.autoReplyRulesShort")}</div>
                    <div>• {i18n.t("mainDrawer.listItems.autoReplyFlowsShort")}</div>
                    <div>• {i18n.t("mainDrawer.listItems.followUpRules")}</div>
                  </>
                )
              }
              placement="right"
            >
              <ListItem button onClick={handleToggleAutomations}>
                <ListItemIcon>
                  <FolderOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary={i18n.t("mainDrawer.listItems.automations")} />
                {automationsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
            </Tooltip>
            <Collapse in={automationsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemLink
                  to="/autoReplyRules"
                  primary={i18n.t("mainDrawer.listItems.autoReplyRulesShort")}
                  icon={<FlashOnOutlinedIcon />}
                  className={classes.nested}
                  drawerOpen={drawerOpen}
                />
                <ListItemLink
                  to="/autoReplyFlows"
                  primary={i18n.t("mainDrawer.listItems.autoReplyFlowsShort")}
                  icon={<DynamicFeedOutlinedIcon />}
                  className={classes.nested}
                  drawerOpen={drawerOpen}
                />
                <ListItemLink
                  to="/followUpRules"
                  primary={i18n.t("mainDrawer.listItems.followUpRules")}
                  icon={<AlarmOutlinedIcon />}
                  className={classes.nested}
                  drawerOpen={drawerOpen}
                />
              </List>
            </Collapse>
            <Tooltip
              title={
                drawerOpen ? "" : (
                  <>
                    <div>Agente</div>
                    <div>• Agentes</div>
                    <div>• Entrenamiento</div>
                    <div>• Documentación</div>
                  </>
                )
              }
              placement="right"
            >
              <ListItem button onClick={handleToggleAgentIa}>
                <ListItemIcon>
                  <AndroidOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary="Agente" />
                {agentIaOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
            </Tooltip>
            <Collapse in={agentIaOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemLink
                  to="/agentIa/agents"
                  primary="Agentes"
                  icon={<GroupOutlinedIcon />}
                  className={classes.nested}
                  drawerOpen={drawerOpen}
                />
                <ListItemLink
                  to="/agentIa/training"
                  primary="Entrenamiento"
                  icon={<ForumIcon />}
                  className={classes.nested}
                  drawerOpen={drawerOpen}
                />
                <ListItemLink
                  to="/agentIa/documents"
                  primary="Documentación"
                  icon={<DescriptionOutlinedIcon />}
                  className={classes.nested}
                  drawerOpen={drawerOpen}
                />
              </List>
            </Collapse>
            <ListItemLink
              to="/settings"
              primary={i18n.t("mainDrawer.listItems.settings")}
              icon={<SettingsOutlinedIcon />}
              drawerOpen={drawerOpen}
            />
          </>
        )}
      />
    </div>
  );
};

export default MainListItems;